/**
 * MKS-393 — the frontend mirror of `backend/src/constants/merchSizeCharts.ts`.
 *
 * Everything from the first `export` below is byte-identical to that file and
 * `backend/tests/merchSizeChartConstantsParity.test.ts` fails CI when the two
 * drift. Edit the backend copy first, then paste it here; do not hand-edit one
 * side only.
 *
 * Why a mirror and not an import: `backend/Dockerfile` and
 * `frontend/Dockerfile` are each built with their own directory as the Docker
 * context (`docker-compose.production.yml:9` and `:36`), so neither image
 * contains the other tree or `../shared`.
 */
export const MERCH_CHART_ITEMS = [
  'tshirt_men',
  'tshirt_women',
  'hoodie_men',
  'hoodie_women',
  'socks',
  'shoes',
] as const;

export type MerchChartItem = (typeof MERCH_CHART_ITEMS)[number];

/** What HR reads: the slot's name in the size-chart manager. */
export const MERCH_CHART_ITEM_LABELS: Record<MerchChartItem, string> = {
  tshirt_men: "T-shirt (men's)",
  tshirt_women: "T-shirt (women's)",
  hoodie_men: "Hoodie (men's)",
  hoodie_women: "Hoodie (women's)",
  socks: 'Socks',
  shoes: 'Shoes',
};

/**
 * The two garments whose chart depends on the cut a person picked, and which
 * slot each cut resolves to. `men` is also what a vendor prints as "unisex",
 * which is why /profile treats the two the same.
 */
export const MERCH_CHART_CUT_ITEMS = {
  tshirt: { men: 'tshirt_men', women: 'tshirt_women' },
  hoodie: { men: 'hoodie_men', women: 'hoodie_women' },
} as const satisfies Record<string, Record<'men' | 'women', MerchChartItem>>;

export type MerchChartGarment = keyof typeof MERCH_CHART_CUT_ITEMS;

/**
 * Which chart slots /profile offers next to a garment's size field.
 *
 * A chosen cut narrows it to that cut's chart alone — a woman is never shown
 * the men's table. With no cut chosen yet there is nothing to narrow by, so
 * BOTH are offered and the person picks; each link still renders only if that
 * slot actually has a chart, which is the caller's existence check, not this
 * function's.
 */
export function merchChartItemsForCut(
  garment: MerchChartGarment,
  cut: 'men' | 'women' | null | undefined,
): readonly MerchChartItem[] {
  const slots = MERCH_CHART_CUT_ITEMS[garment];
  if (cut === 'women') return [slots.women];
  if (cut === 'men') return [slots.men];
  return [slots.men, slots.women];
}

/**
 * The text of one "Size chart" link. Unqualified when it is the only link
 * beside the field, and cut-qualified only when both are offered side by side
 * and the person has to tell them apart.
 */
export function merchChartLinkLabel(item: MerchChartItem, ambiguous: boolean): string {
  if (!ambiguous) return 'Size chart';
  return item.endsWith('_women') ? "Women's size chart" : "Men's size chart";
}

/**
 * Accepted upload types: PNG, JPG and PDF, as the ticket specifies.
 *
 * All three are already in `fileUploadService`'s `DOCUMENT_UPLOAD_MIME_TYPES`
 * (so `uploadFile` admits them on the strict allowlist, with no
 * `allowAnyContentType` escape hatch) AND in its
 * `INLINE_VIEWABLE_CONTENT_TYPES` (so a signed URL renders in the browser
 * instead of downloading). That overlap is what makes "image in a lightbox,
 * PDF in a new tab" work without a single new MIME being admitted anywhere.
 *
 * `image/svg+xml` is NOT here and must never be added: it is script-bearing,
 * which is exactly why `fileUploadService` keeps it out of both sets.
 */
export const MERCH_CHART_MIME_TYPES = ['image/png', 'image/jpeg', 'application/pdf'] as const;

export type MerchChartMimeType = (typeof MERCH_CHART_MIME_TYPES)[number];

/** `accept` for the file input, and the human list in the upload hint. */
export const MERCH_CHART_ACCEPT = MERCH_CHART_MIME_TYPES.join(',');

/** Per-chart upload ceiling. Matches `fileUploadService.MAX_FILE_SIZE_BYTES`. */
export const MERCH_CHART_MAX_BYTES = 10 * 1024 * 1024;

/** True for a content type a chart may be stored as. */
export function isMerchChartMimeType(value: string): value is MerchChartMimeType {
  return (MERCH_CHART_MIME_TYPES as readonly string[]).includes(value);
}

/** True for one of the six slot slugs — the route param guard. */
export function isMerchChartItem(value: string): value is MerchChartItem {
  return (MERCH_CHART_ITEMS as readonly string[]).includes(value);
}

/**
 * How a chart opens. PDFs get a new tab (the browser's own sandboxed viewer);
 * everything else is a raster image and opens in the lightbox. Derived here
 * rather than re-tested in the component so both sides agree.
 */
export function opensInNewTab(contentType: string): boolean {
  return contentType === 'application/pdf';
}

/** What /profile and the HR card are told about one item's chart. */
export interface MerchChartSummary {
  item: MerchChartItem;
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}
